import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, DatePicker, message, Space } from 'antd';
import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import {
    ColDef,
    GridReadyEvent,
    ValueFormatterParams,
    ValueParserParams,
    ValueGetterParams,
    ValueSetterParams,
    CellClassParams,
    ITooltipParams,
    GridApi,
    RowSelectionOptions
} from "ag-grid-community";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './DirectDebitManagement.css';
import dayjs from 'dayjs';
import { useClientConfig } from '../../common/client/ClientConfigProvider';
import { alignCollectionDate, nextCollectionDate } from '../../utils/billingCycle';
import { useMemberService } from "../../services/members/MemberServiceProvider";
import { usePaymentService } from "../../services/payments/PaymentServiceProvider";
import { DirectDebitMemberProfile, AdditionalDirectDebitItem } from "../../model/payments/DirectDebitMemberProfile";
import {
    calculateTotalPaymentAmount,
    effectivePaymentAmount,
    getCollectableAdditionalItems,
} from './directDebitAmounts';
import { CreateDirectDebitFileRequest } from "../../requests/payments/CreateDirectDebitFileRequest";
import { CreateDirectDebitPaymentRequest } from "../../requests/payments/CreateDirectDebitPaymentRequest";
import { CreateNewDirectDebitsBankFileRequest } from "../../requests/payments/CreateNewDirectDebitsBankFileRequest";
import { RollOverMembersRequest } from '../../requests/member/RollOverMembersRequest';
import GenerateBankFileModal from './GenerateBankFileModal';
import DirectDebitRunSummary, { DirectDebitRunSummaryData } from './DirectDebitRunSummary';
import { useFormatCurrency } from '../../hooks/useFormatters';

// const browserLocale = new Intl.Locale(navigator.language);
// const isUK = browserLocale.baseName.startsWith('en-GB');

// Helper function to compare only date parts (ignoring time)
const isDateOnOrBefore = (date1: Date, date2: Date): boolean => {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return d1.getTime() <= d2.getTime();
};

const isDateOn = (date1: Date, date2: Date): boolean => {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return d1.getTime() === d2.getTime();
};

// Helper function to format date as YYYY-MM-DD
const formatDateForFilename = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

const DirectDebitManagement: React.FC = () => {
    const paymentService = usePaymentService();
    const memberService = useMemberService();
    const { billing } = useClientConfig();
    const [memberProfiles, setMemberProfiles] = useState<DirectDebitMemberProfile[]>([]);
    // Default the collection date to the client's next billing cycle day (the
    // backend snaps to it anyway; this keeps the picker consistent).
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(nextCollectionDate(billing));
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [manualGridApi, setManualGridApi] = useState<GridApi | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [manualSelectionCount, setManualSelectionCount] = useState<number>(0);
    const [directDebitMemberCount, setDirectDebitMemberCount] = useState<number>(0);
    const [generateBankFileModalVisible, setGenerateBankFileModalVisible] = useState<boolean>(false);
    const [overriddenAmounts, setOverriddenAmounts] = useState<Map<string, number>>(new Map());
    const [directDebitSelectionSummary, setDirectDebitSelectionSummary] = useState<DirectDebitRunSummaryData>({
        count: 0,
        totalAmount: 0,
        categoryBreakdown: {},
    });
    const [manualSelectionSummary, setManualSelectionSummary] = useState<DirectDebitRunSummaryData>({
        count: 0,
        totalAmount: 0,
        categoryBreakdown: {},
    });

    useEffect(() => {
        // .catch, not try/catch: a try around a promise chain cannot catch an async
        // rejection, so the block that used to sit here was dead code. The API client
        // notifies on failure (showError), so this only has to keep the rejection handled.
        paymentService
            .getActiveDirectDebitMembers()
            .then(setMemberProfiles)
            .catch(() => { /* notified by the API client */ });
    }, [memberService, paymentService]);

    // Filter members with direct debit information
    const membersWithDirectDebit = useMemo(() => {
        console.log('Filtering membersWithDirectDebit from total:', memberProfiles.length);
        const filtered =  memberProfiles.filter(member => {            
            const instruction = member.directDebitPayment;
            const isMonthly = member.profile.membershipCategory.paymentFrequency === 'Monthly';
            const isNotExpired = member.profile.membershipCategory.name !== 'Expired';
            const hasAdditionalDirectDebitItems = member.additionalDirectDebitItems.length > 0;
            const hasDirectDebit = instruction && 
                   instruction.accountHolderName &&                    
                   instruction.accountNumber && 
                   instruction.sortCode &&
                   instruction.accountHolderName.trim() !== '' &&                   
                   instruction.accountNumber.trim() !== '' &&
                   instruction.sortCode.trim() !== '';
            
            if (!hasDirectDebit) {
                console.log('Member filtered out:', member.profile.name, 'hasDirectDebit:', !!instruction, 'isMonthly:', isMonthly);
            }
            
            return (isMonthly || hasAdditionalDirectDebitItems) && hasDirectDebit && isNotExpired;
        });
        console.log('Members with direct debit information count:', filtered.length);
        return filtered;
    }, [memberProfiles]);

    // Filter members without direct debit information
    const manualPaymentMembers = useMemo(() => {
        console.log('Filtering manualPaymentMembers from total:', memberProfiles.length);
        const filtered = memberProfiles.filter(member => {
            const instruction = member.directDebitPayment;

            const isNotAnnual = member.profile.membershipCategory.paymentFrequency !== 'Annual';            
            const isMonthly = member.profile.membershipCategory.paymentFrequency === 'Monthly';
            const isNotExpired = member.profile.membershipCategory.name !== 'Expired';
            const hasNoDirectDebit = !instruction || 
                   !instruction.accountHolderName ||                    
                   !instruction.accountNumber || 
                   !instruction.sortCode ||
                   instruction.accountHolderName.trim() === '' ||                   
                   instruction.accountNumber.trim() === '' ||
                   instruction.sortCode.trim() === '';

            const isEnabled = member.profile.isEnabled;
            
            if (!hasNoDirectDebit || !isEnabled || !isNotAnnual || !isMonthly) {
                console.log('Member filtered out:', member.profile.name, 'hasNoDirectDebit:', hasNoDirectDebit, 'isEnabled:', isEnabled, 'isNotAnnual:', isNotAnnual);
            }

            return hasNoDirectDebit && isEnabled && isNotAnnual && isMonthly && isNotExpired;
        });
        console.log('Manual payment members count:', filtered.length);
        return filtered;
    }, [memberProfiles]);

    const formatCurrency = useFormatCurrency();

    // The date the run will actually collect on. The API snaps the operator's pick onto
    // the club's billing cycle day, so judging item validity (or totalling an amount)
    // against the raw pick would show — and send — money for a different date than the
    // one the file collects on.
    const collectionDate = useMemo(
        () => alignCollectionDate(selectedDate, billing),
        [selectedDate, billing]
    );
    const collectionDateIsSnapped = !collectionDate.isSame(selectedDate, 'day');


    const currencyFormatter = useCallback((params: ValueFormatterParams) => {
        if (typeof params.value === 'number') {
            return formatCurrency(params.value);
        }
        return '';
    }, [formatCurrency]);


    const currencyParser = useCallback((params: ValueParserParams) => {
        if (typeof params.newValue === 'string') {
            const numberValue = Number(params.newValue.replace(/[^0-9.-]+/g, ""));
            return isNaN(numberValue) ? params.oldValue : numberValue;
        }
        return params.oldValue;
    }, []);

    const recalculateSelectionSummary = useCallback(
        (members: DirectDebitMemberProfile[]): DirectDebitRunSummaryData => {
            const paymentDate = collectionDate.toDate();
            const categoryBreakdown: Record<string, number> = {};
            let totalAmount = 0;

            members.forEach((member) => {
                const categoryName = member.profile.membershipCategory.name;
                const amount = effectivePaymentAmount(member, paymentDate, overriddenAmounts);

                totalAmount += amount;
                categoryBreakdown[categoryName] = (categoryBreakdown[categoryName] || 0) + amount;
            });

            return {
                count: members.length,
                totalAmount,
                categoryBreakdown,
            };
        },
        [collectionDate, overriddenAmounts]
    );

    const additionalDirectDebitItemsFormatter = useCallback((params: ValueFormatterParams<DirectDebitMemberProfile, AdditionalDirectDebitItem[]>) => {
        if (!params.value || !Array.isArray(params.value)) {
            return '';
        }

        const collectableItems = getCollectableAdditionalItems(params.value, collectionDate.toDate());

        // Format as "description:amount" and join with commas
        return collectableItems
            .map(item => `${item.description}: ${currencyFormatter({ value: item.amount } as ValueFormatterParams)}`)
            .join(', ');
    }, [collectionDate, currencyFormatter]);

    /**
     * What this row will collect — the override if the operator set one, else the
     * calculated total. This is a valueGetter, not a raw `field`, deliberately:
     *
     *  - the editor opens on the amount the operator can *see* (sub + items), rather
     *    than on the bare category price the cell never displayed; and
     *  - AG Grid compares the typed value against this same number to decide whether
     *    anything changed, so typing "the subscription only" over a total that
     *    includes an extra item registers as an edit. Bound to the category price it
     *    did not: the two matched, AG Grid called it a no-op, and the override was
     *    silently dropped.
     */
    const paymentAmountGetter = useCallback((params: ValueGetterParams<DirectDebitMemberProfile>): number | null => {
        if (!params.data) return null;
        return effectivePaymentAmount(params.data, collectionDate.toDate(), overriddenAmounts);
    }, [collectionDate, overriddenAmounts]);

    const paymentAmountSetter = useCallback((params: ValueSetterParams<DirectDebitMemberProfile>): boolean => {
        if (!params.data) return false;
        const membershipId = params.data.profile.membershipId;
        const newAmount = params.newValue;

        setOverriddenAmounts(prev => {
            const next = new Map(prev);
            if (typeof newAmount === 'number' && !isNaN(newAmount)) {
                next.set(membershipId, newAmount);
            } else {
                // Cleared the cell — drop the override and fall back to the calculated total.
                next.delete(membershipId);
            }
            return next;
        });

        return true;
    }, []);


    const columnDefs = useMemo<ColDef<DirectDebitMemberProfile>[]>(() => [
        {
            headerName: 'Name',
            field: 'profile.name',
            filter: 'agTextColumnFilter',
            editable: false,
        },
        {
            headerName: 'Email',
            field: 'profile.emailAddress',
            filter: 'agTextColumnFilter',
            editable: false,
        },
        {
            headerName: 'Status',
            field: 'profile.status',
            filter: 'agTextColumnFilter',
            editable: false,
        },
        {
            headerName: 'Membership ID',
            field: 'profile.membershipId',
            filter: 'agTextColumnFilter',
            editable: false,
        },
        {
            headerName: 'Payment Amount',
            colId: 'paymentAmount',
            headerTooltip: 'Click to type a different amount for this run. Clear the cell to go back to the calculated total.',
            filter: 'agNumberColumnFilter',
            editable: true,
            // One click, not two: an amount you can only reach by discovering that the
            // cell is double-clickable is an amount operators do not override.
            singleClickEdit: true,
            valueGetter: paymentAmountGetter,
            valueSetter: paymentAmountSetter,
            valueFormatter: currencyFormatter,
            valueParser: (params: ValueParserParams) => currencyParser(params),
            // Say so on the cell itself: an override that looks identical to a calculated
            // total is one nobody can audit before the file goes to the bank.
            tooltipValueGetter: (params: ITooltipParams<DirectDebitMemberProfile>) => {
                if (!params.data) return '';
                const calculated = calculateTotalPaymentAmount(params.data, collectionDate.toDate());
                return overriddenAmounts.has(params.data.profile.membershipId)
                    ? `Overridden for this run. Calculated total is ${formatCurrency(calculated)} — clear the cell to restore it.`
                    : 'Click to override the amount collected for this run.';
            },
            cellClassRules: {
                'dd-amount-overridden': (params: CellClassParams<DirectDebitMemberProfile>) =>
                    !!params.data && overriddenAmounts.has(params.data.profile.membershipId),
            },
            minWidth: 170,
        },
        {
            headerName: 'Membership Category',
            field: 'profile.membershipCategory.name',
            filter: 'agTextColumnFilter',
            editable: false,
        },
        {
            headerName: 'Valid To',
            field: 'profile.validTo',
            filter: 'agDateColumnFilter',
            editable: false,
            valueFormatter: (params: ValueFormatterParams<DirectDebitMemberProfile, Date>) => {
                if (params.value) {
                    return dayjs(params.value).format('DD/MM/YYYY');
                }
                return '';
            },
        },
        {
            headerName: 'Cancellation Date',
            colId: 'cancellationDate',
            filter: 'agDateColumnFilter',
            editable: false,
            valueGetter: (params) => {
                const cancellation = params.data?.cancellation;
                if (!cancellation || !cancellation.cancellationActive || !cancellation.cancellationDate) {
                    return null;
                }
                return new Date(cancellation.cancellationDate);
            },
            valueFormatter: (params: ValueFormatterParams<DirectDebitMemberProfile, Date>) => {
                if (params.value) {
                    return dayjs(params.value).format('DD/MM/YYYY');
                }
                return '';
            },
        },
        {
            headerName: 'Additional Direct Debit Items',
            field: 'additionalDirectDebitItems',
            filter: 'agTextColumnFilter',
            editable: false,
            valueFormatter: additionalDirectDebitItemsFormatter,
            minWidth: 200,
        },
    ], [currencyParser, currencyFormatter, additionalDirectDebitItemsFormatter, paymentAmountGetter,
        paymentAmountSetter, overriddenAmounts, collectionDate, formatCurrency]);

    const onGridReady = useCallback((params: GridReadyEvent<DirectDebitMemberProfile>) => {
        setGridApi(params.api);
    }, []);

    const onManualGridReady = useCallback((params: GridReadyEvent<DirectDebitMemberProfile>) => {
        setManualGridApi(params.api);
    }, []);

    const onDirectDebitSelectionChanged = useCallback(() => {
        if (gridApi) {
            const selectedRows = gridApi.getSelectedRows() as DirectDebitMemberProfile[];
            setDirectDebitSelectionSummary(recalculateSelectionSummary(selectedRows));
        }
    }, [gridApi, recalculateSelectionSummary]);

    const onManualSelectionChanged = useCallback(() => {
        if (manualGridApi) {
            const selectedRows = manualGridApi.getSelectedRows() as DirectDebitMemberProfile[];
            setManualSelectionCount(selectedRows.length);
            setManualSelectionSummary(recalculateSelectionSummary(selectedRows));
        }
    }, [manualGridApi, recalculateSelectionSummary]);

    // The summary is where an operator checks the run's total before the file goes to the
    // bank, and it only recalculated on selection. An override — now a single click away —
    // changed what gets sent without changing what was shown.
    useEffect(() => {
        if (gridApi) {
            setDirectDebitSelectionSummary(recalculateSelectionSummary(gridApi.getSelectedRows()));
        }
        if (manualGridApi) {
            setManualSelectionSummary(recalculateSelectionSummary(manualGridApi.getSelectedRows()));
        }
    }, [gridApi, manualGridApi, recalculateSelectionSummary]);

    const isCancelledOnOrBefore = useCallback((member: DirectDebitMemberProfile, paymentDate: Date): boolean => {
        const cancellation = member.cancellation;
        if (!cancellation || !cancellation.cancellationActive || !cancellation.cancellationDate) {
            return false;
        }
        const cancellationDate = new Date(cancellation.cancellationDate);
        return isDateOnOrBefore(cancellationDate, paymentDate);
    }, []);

    const selectExpiringMembers = useCallback(() => {
        if (gridApi) {
            // Clear all current selections first
            gridApi.deselectAll();
            let count = 0;
            const selectedDateAsDate = collectionDate.toDate();
            gridApi.forEachNode((node) => {
                const isMonthly = node.data.profile.membershipCategory.paymentFrequency === 'Monthly';
                const isNotExpired = node.data.profile.membershipCategory.name !== 'Expired';
                const isExpiringOnPaymentDate = node.data.profile.validTo && isDateOn(node.data.profile.validTo, selectedDateAsDate);
                const isCancelledExpiring = isExpiringOnPaymentDate && isCancelledOnOrBefore(node.data, selectedDateAsDate);
                if (node.data.profile.validTo && isExpiringOnPaymentDate && node.data.profile.isEnabled && isMonthly && isNotExpired && !isCancelledExpiring) {
                    node.setSelected(true);
                    count++;
                }
            });
            setDirectDebitMemberCount(count);
        }
    }, [gridApi, collectionDate, isCancelledOnOrBefore]);

    const selectExpiringManualMembers = useCallback(() => {
        if (manualGridApi) {
            // Clear all current selections first
            manualGridApi.deselectAll();

            const selectedDateAsDate = collectionDate.toDate();
            manualGridApi.forEachNode((node) => {
                const isMonthly = node.data.profile.membershipCategory.paymentFrequency === 'Monthly';
                const isNotExpired = node.data.profile.membershipCategory.name !== 'Expired';
                const isExpiringOnPaymentDate = node.data.profile.validTo && isDateOn(node.data.profile.validTo, selectedDateAsDate);
                const isCancelledExpiring = isExpiringOnPaymentDate && isCancelledOnOrBefore(node.data, selectedDateAsDate);
                if (node.data.profile.validTo && isExpiringOnPaymentDate && node.data.profile.isEnabled && isMonthly && isNotExpired && !isCancelledExpiring) {
                    node.setSelected(true);
                }
            });
        }
    }, [manualGridApi, collectionDate, isCancelledOnOrBefore]);

    const rollOverSelectedMembers = async () => {
        if (!manualGridApi) return;

        const selectedNodes = manualGridApi.getSelectedRows();
        if (selectedNodes.length === 0) {
            message.warning('Please select at least one member to roll over');
            return;
        }

        setLoading(true);
        try {
            const request: RollOverMembersRequest = {
                membershipIds: selectedNodes.map((member: DirectDebitMemberProfile) => member.profile.membershipId),
                asOfDate: collectionDate.format('YYYY-MM-DD'),
                rollOverType: 'ManualPaymentMembers'
            };
            
            await memberService.rollOverMembers(request);
            message.success(`Successfully rolled over ${selectedNodes.length} member(s)`);
            
            // Refresh the member profiles after roll over
            const updatedProfiles = await paymentService.getActiveDirectDebitMembers();
            setMemberProfiles(updatedProfiles);
            
        } catch (error) {
            console.error('Failed to roll over members:', error);
            message.error('Failed to roll over members');
        } finally {
            setLoading(false);
        }
    };

    const rowSelection = useMemo<
        RowSelectionOptions | "single" | "multiple"
        >(() => {
        return {
            mode: "multiRow",
            checkboxes: true,
            headerCheckbox: true,
            enableClickSelection: true,
        };
        }, []);

    const createDirectDebitFile = async () => {
        if (!gridApi) return;

        const selectedNodes = gridApi.getSelectedRows();
        if (selectedNodes.length === 0) {
            message.warning('Please select at least one member with direct debit information');
            return;
        }

        setLoading(true);
        try {
            const selectedDateAsDate = collectionDate.toDate();

            const request: CreateDirectDebitFileRequest = {
                paymentDate: selectedDateAsDate,
                directDebitPaymentRequests: selectedNodes.map((member: DirectDebitMemberProfile): CreateDirectDebitPaymentRequest => ({
                    membershipId: member.profile.membershipId,
                    paymentDate: selectedDateAsDate,
                    amount: effectivePaymentAmount(member, selectedDateAsDate, overriddenAmounts),
                })),
            };
            const fileBlob = await paymentService.createDirectDebitFile(request);

            const url = window.URL.createObjectURL(fileBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `direct_debit_${formatDateForFilename(selectedDateAsDate)}.txt`;
            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            message.success('Direct debit file created successfully');
        } catch (error) {
            console.error('Failed to create direct debit file:', error);
            message.error('Failed to create direct debit file');
        } finally {
            setLoading(false);
        }
    };

    const generateBankFile = async (asOfDate: dayjs.Dayjs) => {
        setLoading(true);
        try {
            const request: CreateNewDirectDebitsBankFileRequest = {
                asOfUtc: asOfDate.toISOString()                
            };
            
            const fileBlob = await paymentService.createNewDirectDebitsBankFile(request);

            const url = window.URL.createObjectURL(fileBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `new_direct_debits_${formatDateForFilename(asOfDate.toDate())}.txt`;
            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            message.success('Bank file generated successfully');
            setGenerateBankFileModalVisible(false);
        } catch (error) {
            console.error('Failed to generate bank file:', error);
            message.error('Failed to generate bank file');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '24px' }}>
            <h1 style={{ fontSize: '24px', marginBottom: '16px' }}>Direct Debit Management</h1>
            <div style={{ marginBottom: '16px', color: '#666' }}>
                Total Members: {memberProfiles.length} | 
                Direct Debit Members: {membersWithDirectDebit.length} | 
                Manual Payment Members: {manualPaymentMembers.length}
            </div>
            <div style={{ marginBottom: '16px', color: '#999', fontSize: '12px' }}>
                Raw Data: {memberProfiles.length} profiles loaded from API
            </div>
            <Space style={{ marginBottom: '16px' }}>
                <DatePicker 
                    value={selectedDate}
                    onChange={(newDate) => newDate && setSelectedDate(newDate)}
                    format="DD/MM/YYYY"
                    allowClear={false}
                    style={{ width: '180px' }}
                />
                {collectionDateIsSnapped && (
                    <span data-testid="dd-collection-date-snapped" style={{ color: '#ad6800', fontSize: '12px' }}>
                        Collects on {collectionDate.format('DD/MM/YYYY')} — the club's billing cycle day.
                    </span>
                )}
                <Button
                    data-testid="dd-create-file"
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={createDirectDebitFile}
                    loading={loading}
                >
                    Create Direct Debit File
                </Button>
                <Button
                    type="default"
                    icon={<FileTextOutlined />}
                    onClick={() => setGenerateBankFileModalVisible(true)}
                    loading={loading}
                >
                    Generate Bank File
                </Button>
            </Space>
            
            {/* Direct Debit Members Section */}
            <div style={{ marginBottom: '32px' }}>
                <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>Direct Debit Members ({membersWithDirectDebit.length})</h2>
                <div style={{ marginBottom: '16px' }}>                
                    <Button data-testid="dd-select-expiring" onClick={selectExpiringMembers}>Select Expiring Members [{directDebitMemberCount}]</Button>
                </div>
                <DirectDebitRunSummary
                    summary={directDebitSelectionSummary}
                    countLabel="Selected direct debits"
                    totalLabel="Total amount this run"
                />
                <div className="dd-amount-hint" data-testid="dd-amount-hint-auto">
                    Payment Amount is the membership subscription plus every recurring item still running on the
                    collection date. Click the cell to collect a different amount this run; clear it to restore the
                    calculated total.
                </div>

                <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                    <AgGridReact
                        columnDefs={columnDefs}
                        rowData={membersWithDirectDebit}
                        onGridReady={onGridReady}
                        onSelectionChanged={onDirectDebitSelectionChanged}
                        defaultColDef={{
                            flex: 1,
                            minWidth: 100,
                            filter: true,
                            sortable: true,
                            resizable: true,
                        }}
                        rowSelection={rowSelection}
                        suppressRowClickSelection={true}
                        // Add properties for better handling of large datasets
                        rowBuffer={20}
                        maxBlocksInCache={10}
                        blockLoadDebounceMillis={100}
                        suppressAnimationFrame={false}
                        suppressColumnVirtualisation={false}
                        suppressRowVirtualisation={false}
                        // Ensure all rows are loaded
                        pagination={false}
                        paginationPageSize={1000}
                    />
                </div>
            </div>

            {/* Manual Payment Members Section */}
            <div style={{ marginBottom: '32px' }}>
                <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>Manual Payment Members ({manualPaymentMembers.length})</h2>
                <div style={{ marginBottom: '16px' }}>                
                    <Button onClick={selectExpiringManualMembers}>Select Expiring Members</Button>
                    <Button 
                        type="primary"
                        style={{ 
                            marginLeft: '8px', 
                            backgroundColor: manualSelectionCount === 0 ? '#d9d9d9' : '#52c41a', 
                            borderColor: manualSelectionCount === 0 ? '#d9d9d9' : '#52c41a',
                            color: manualSelectionCount === 0 ? '#bfbfbf' : '#ffffff',
                            fontWeight: manualSelectionCount === 0 ? 'normal' : 'bold'
                        }}
                        onClick={rollOverSelectedMembers}
                        disabled={manualSelectionCount === 0}
                        loading={loading}
                    >
                        Roll Over Selected Members ({manualSelectionCount})
                    </Button>
                </div>
                <DirectDebitRunSummary
                    summary={manualSelectionSummary}
                    countLabel="Selected members"
                    totalLabel="Total amount this run"
                />
                <div className="dd-amount-hint" data-testid="dd-amount-hint-manual">
                    Payment Amount is the membership subscription plus every recurring item still running on the
                    collection date. Click the cell to collect a different amount this run; clear it to restore the
                    calculated total.
                </div>

                <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                    <AgGridReact
                        columnDefs={columnDefs}
                        rowData={manualPaymentMembers}
                        onGridReady={onManualGridReady}
                        onSelectionChanged={onManualSelectionChanged}
                        defaultColDef={{
                            flex: 1,
                            minWidth: 100,
                            filter: true,
                            sortable: true,
                            resizable: true,
                        }}
                        rowSelection={rowSelection}
                        suppressRowClickSelection={true}
                        // Add properties for better handling of large datasets
                        rowBuffer={20}
                        maxBlocksInCache={10}
                        blockLoadDebounceMillis={100}
                        suppressAnimationFrame={false}
                        suppressColumnVirtualisation={false}
                        suppressRowVirtualisation={false}
                        // Ensure all rows are loaded
                        pagination={false}
                        paginationPageSize={1000}
                    />
                </div>
            </div>

            {/* Generate Bank File Modal */}
            <GenerateBankFileModal
                visible={generateBankFileModalVisible}
                onCancel={() => setGenerateBankFileModalVisible(false)}
                onConfirm={generateBankFile}
                loading={loading}
            />
        </div>
    );
};

export default DirectDebitManagement;
